import { useEffect, useState, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useWebSocketContext } from 'context/WebSocketConnectionContext';
import { getTenantDetails } from 'utils/utils';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getBillingCode } from 'services/timerServices';

export default function useAutomatedTime(case_id, case_name) {
  const { sendMessage, timeEntryData } = useWebSocketContext();
  const { firmDetails, profileData } = useUserDetailsContext();
  const [tenant_id] = getTenantDetails();

  const [timerParameter, setTimerParameter] = useState({});
  const isMountedRef = useRef(false);

  const isActive = profileData?.automatic_time_tracking_active;

  useEffect(() => {
    //Heartbeat to make the websocket connection alive, prevent diconnection due to inactivity
    //calls every 5 minutes
    let intervel = setInterval(() => {
      isActive &&
        sendMessage({
          type: 'heartbeat',
          action: 'message',
          data: {
            tenant_id: tenant_id,
          },
        });
    }, 1000 * 60 * 5);

    return () => {
      clearInterval(intervel);
    };
  }, [isActive]);

  const getActivityBillingCode = async (moduleName) => {
    try {
      const response = await getBillingCode(moduleName, true);
      if (response?.data?.mapping_type) {
        return response.data.mapping_type[0];
      }
    } catch (err) {}
    return null;
  };

  const onIdle = async () => {
    // Check if still mounted and has active time entry
    if (!isMountedRef.current || !isActive || !timerParameter?.time_entry_sk) {
      return;
    }

    const billingCode = await getActivityBillingCode(timerParameter?.module);

    // Double-check after async operation
    if (isMountedRef.current && isActive && timerParameter?.time_entry_sk) {
      sendMessage({
        action: 'timeentry',
        time_entry_type: 'idle_start',
        tenant_id: tenant_id,
        user_id: firmDetails?.firm_user_id,
        case_id: case_id,
        time_entry_sk: timerParameter?.time_entry_sk,
        module_name: timerParameter?.module,
        activity_type: billingCode?.activity_type,
        activity_type_code: billingCode?.activity_type_code,
        activity_type_id: billingCode?.activity_type_id,
      });
    }
  };

  const onActive = () => {
    // Check if still mounted and has active time entry with idle tracking
    if (!isMountedRef.current || !isActive || !timerParameter?.time_entry_sk || !timerParameter?.idle_time_entry_sk) {
      return;
    }

    sendMessage({
      action: 'timeentry',
      time_entry_type: 'idle_end',
      tenant_id: tenant_id,
      user_id: firmDetails?.firm_user_id,
      case_id: case_id,
      time_entry_sk: timerParameter?.time_entry_sk,
      idle_time_entry_sk: timerParameter?.idle_time_entry_sk,
    });
  };

  const onOperationBreak = () => {
    // delay added to avoid calling operation break before the timer end operation
    setTimeout(() => {
      if (isActive) {
        sendMessage({
          action: 'timeentry',
          time_entry_type: 'operation_break',
        });
        setTimerParameter({});
        reset();
      }
    }, 2000);
  };

  const { reset, start, pause } = useIdleTimer({
    onIdle,
    onActive,
    timeout: 10 * 60 * 1000,
    throttle: 500,
  });

  useEffect(() => {
    if (timeEntryData) {
      setTimerParameter((preVal) => ({
        ...preVal,
        time_entry_sk: timeEntryData?.body?.time_entry_sk || preVal?.time_entry_sk,
        idle_time_entry_sk: timeEntryData?.body?.idle_time_entry_sk || preVal?.idle_time_entry_sk,
      }));
    }
  }, [timeEntryData]);

  const onMountUnMount = async (moduleName, action, caseId) => {
    let oldData = {};
    let billingCode = {};
    setTimerParameter((preVal) => {
      oldData = preVal;
      return preVal;
    });
    if (action === 'mounted' && isActive) {
      isMountedRef.current = true;
      billingCode = await getActivityBillingCode(moduleName);
      sendMessage({
        action: 'timeentry',
        time_entry_type: 'time_entry_start',
        tenant_id: tenant_id,
        user_id: firmDetails?.firm_user_id,
        case_name: case_name,
        case_id: caseId || case_id,
        module_name: moduleName,
        activity_type: billingCode?.activity_type,
        activity_type_code: billingCode?.activity_type_code,
        activity_type_id: billingCode?.activity_type_id,
      });
      start();
    } else if (action === 'unmounted' && isActive) {
      // Mark as unmounted first to prevent any callbacks from executing
      isMountedRef.current = false;
      // Pause the timer to prevent onIdle from being called
      pause();
      sendMessage({
        action: 'timeentry',
        time_entry_type: 'time_entry_end',
        tenant_id: tenant_id,
        user_id: firmDetails?.firm_user_id,
        case_name: case_name,
        case_id: caseId || case_id,
        time_entry_sk: oldData?.time_entry_sk,
      });
      setTimerParameter({});
      reset();
    }
    if (action === 'mounted') {
      setTimerParameter((preVal) => ({
        ...preVal,
        module: moduleName,
        activity_type: billingCode?.activity_type,
        activity_type_code: billingCode?.activity_type_code,
        activity_type_id: billingCode?.activity_type_id,
      }));
    }
  };

  return { onMountUnMount, onOperationBreak };
}
